import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { AuthContext } from "../../../context/AuthContext";
import { fetchWithAuth } from "../../../utils/api";
import { swalError, swalSuccess, swalWarning } from "../../../utils/swalAlert";
import Loader from "../../ui/Loader";
import ReportCard from "../../ui/ReportCard";
import Table from "../../ui/Table";
import { createRoot } from "react-dom/client";
import { motion, AnimatePresence } from "framer-motion";
import "../../../App.css";

const serverUrl = process.env.REACT_APP_SERVER_URL;

const AdminReportCard = () => {
  const { user } = useContext(AuthContext);
  const instituteId = user?.instituteId;

  const [loadingInitial, setLoadingInitial] = useState(true);
  const [instituteInfo, setInstituteInfo] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [periodData, setPeriodData] = useState(null);
  const [fetchingPeriodData, setFetchingPeriodData] = useState(false);
  const [reportCardData, setReportCardData] = useState([]);
  const lastFetchedPeriodRef = useRef(null);

  const [filters, setFilters] = useState({
    period: "", // Selected period _id
    level: "all",
    testType: [],
  });

  const [isTestTypeMenuOpen, setIsTestTypeMenuOpen] = useState(false);
  const testTypeRef = React.useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (testTypeRef.current && !testTypeRef.current.contains(event.target)) {
        setIsTestTypeMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleTestType = (type) => {
    setFilters((prev) => {
      const current = prev.testType || [];
      const next = current.includes(type)
        ? current.filter((t) => t !== type)
        : [...current, type];
      return { ...prev, testType: next };
    });
  };

  const disableFilters = {}; // For Dashboard layout parity

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      let next = { ...prev, [filterType]: value };
      if (filterType === "period") {
        next.level = "all";
        next.testType = [];
      }
      if (filterType === "level") {
        if (value === "all") {
          next.testType = [];
        } else if (periodData?.classes) {
          const validInNewLevel = new Set(
            periodData.classes
              .filter((c) => c.level === value)
              .flatMap((c) => c.tests || [])
              .map((t) => t?.testType)
              .filter(Boolean),
          );
          next.testType = (prev.testType || []).filter((t) =>
            validInNewLevel.has(t),
          );
        } else {
          next.testType = [];
        }
      }
      return next;
    });
  };

  const periodDisplayMap = useMemo(() => {
    const map = {};
    const sortedPeriods = (periods || []).sort((a, b) => {
      const yearA = parseInt(a.year) || 0;
      const yearB = parseInt(b.year) || 0;
      if (yearA !== yearB) return yearB - yearA;
      return (a.term || "").localeCompare(b.term || "");
    });
    sortedPeriods.forEach((p) => {
      if (p && p._id) {
        map[p._id] = `${p.term}-${p.year}`;
      }
    });
    return map;
  }, [periods]);

  const hasActiveFilters = useMemo(
    () => filters.level !== "all" || filters.testType.length > 0,
    [filters],
  );

  const clearAllFilters = () => {
    setFilters((prev) => ({
      ...prev,
      level: "all",
      testType: [],
    }));
  };

  const [fetchingData, setFetchingData] = useState(false);
  const [generatingPdfTarget, setGeneratingPdfTarget] = useState(null); // null, "all", or studentId
  const [generatingPdfProgress, setGeneratingPdfProgress] = useState(null); // { current, total, format }
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [pendingDownloadStudents, setPendingDownloadStudents] = useState([]);
  const [exportingExcel, setExportingExcel] = useState(false);

  // ── Selection state ──
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());

  // ── Send / Notify state ──
  const [sendingTarget, setSendingTarget] = useState(null); // null, "all", or studentId
  const [sendPhase, setSendPhase] = useState(""); // "preparing", "generating", "sending", "completed"
  const [sendProgress, setSendProgress] = useState("");
  const [currentSendStudentName, setCurrentSendStudentName] = useState("");
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [pendingSendStudents, setPendingSendStudents] = useState([]);

  useEffect(() => {
    if (!instituteId) return;

    const fetchInitialData = async () => {
      try {
        setLoadingInitial(true);
        // 1. Fetch Institute for info + grade mapping
        const instRes = await fetchWithAuth(
          `${serverUrl}/api/institute/get/${instituteId}`,
        );
        if (!instRes.ok) throw new Error("Failed to fetch institute info");
        const instData = await instRes.json();
        setInstituteInfo(instData);

        // 2. Fetch Periods
        const periodRes = await fetchWithAuth(
          `${serverUrl}/api/period/get-all/${instituteId}`,
        );
        let initialPeriod = "";
        if (periodRes.ok) {
          const periodsData = await periodRes.json();
          setPeriods(periodsData || []);
          if (periodsData && periodsData.length > 0) {
            const ongoing = periodsData.find((p) => p.status === "Ongoing");
            initialPeriod = ongoing ? ongoing._id : periodsData[0]._id;
            setFilters((prev) => ({
              ...prev,
              period: initialPeriod,
            }));
          }
        }

        // 3. Fetch data for the initial ongoing period
        if (initialPeriod) {
          try {
            const effectivePeriodId =
              initialPeriod === "all" ? "null" : initialPeriod;
            const periodDataRes = await fetchWithAuth(
              `${serverUrl}/api/institute/get-full-data-by-period/${instituteId}/${effectivePeriodId}`,
            );
            if (periodDataRes.ok) {
              const pData = await periodDataRes.json();
              setPeriodData(pData || { classes: [] });
            }
          } catch (pErr) {
            console.error("Failed to fetch initial period data", pErr);
          }
          lastFetchedPeriodRef.current = initialPeriod;
        }
      } catch (err) {
        swalError(err.message);
      } finally {
        setLoadingInitial(false);
      }
    };

    fetchInitialData();
  }, [instituteId]);

  // Fetch period data whenever selected period changes
  useEffect(() => {
    if (!instituteId || !filters.period) return;
    if (lastFetchedPeriodRef.current === filters.period) {
      lastFetchedPeriodRef.current = null;
      return;
    }

    let isCancelled = false;
    const fetchPeriodData = async () => {
      try {
        setFetchingPeriodData(true);
        const effectivePeriodId =
          filters.period === "all" ? "null" : filters.period;
        const res = await fetchWithAuth(
          `${serverUrl}/api/institute/get-full-data-by-period/${instituteId}/${effectivePeriodId}`,
        );
        if (!res.ok) throw new Error("Failed to fetch period data");
        const data = await res.json();
        if (!isCancelled) {
          setPeriodData(data || { classes: [] });
        }
      } catch (err) {
        if (!isCancelled) {
          console.error("Failed to fetch period data:", err);
          setPeriodData({ classes: [] });
        }
      } finally {
        if (!isCancelled) {
          setFetchingPeriodData(false);
        }
      }
    };

    fetchPeriodData();

    return () => {
      isCancelled = true;
    };
  }, [instituteId, filters.period]);

  // Fetch Report Card Data from dedicated API when all filters are set
  useEffect(() => {
    if (!instituteId || !filters.period || filters.level === "all") {
      setReportCardData([]);
      return;
    }

    let isCancelled = false;
    const fetchReportCardData = async () => {
      try {
        setFetchingData(true);
        const effectivePeriodId =
          filters.period === "all" ? "null" : filters.period;
        const levelParam = encodeURIComponent(filters.level);
        const res = await fetchWithAuth(
          `${serverUrl}/api/institute/get-report-card-data/${instituteId}/${effectivePeriodId}?level=${levelParam}`,
        );
        if (!res.ok) throw new Error("Failed to fetch report card data");
        const data = await res.json();
        if (!isCancelled) {
          setReportCardData(data.students || []);
        }
      } catch (err) {
        if (!isCancelled) {
          swalError(err.message || "Failed to fetch report card data");
          setReportCardData([]);
        }
      } finally {
        if (!isCancelled) {
          setFetchingData(false);
        }
      }
    };
    fetchReportCardData();

    return () => {
      isCancelled = true;
    };
  }, [instituteId, filters.period, filters.level]);

  // Level options derived from fetched period data (filtered by selected testType if any)
  const levelOptions = useMemo(() => {
    if (!periodData?.classes || !Array.isArray(periodData.classes)) {
      return [];
    }

    let candidateClasses = periodData.classes;

    if (filters.testType && filters.testType.length > 0) {
      candidateClasses = candidateClasses.filter((c) =>
        (c.tests || []).some((t) => filters.testType.includes(t.testType)),
      );
    }

    const extractGradeNumber = (level) => {
      const match = (level || "").match(/grade\s+(\d+)/i);
      return match ? parseInt(match[1], 10) : null;
    };

    return [
      ...new Set(candidateClasses.map((c) => c.level).filter(Boolean)),
    ].sort((a, b) => {
      const aGrade = extractGradeNumber(a);
      const bGrade = extractGradeNumber(b);
      if (aGrade !== null && bGrade !== null) return aGrade - bGrade;
      if (aGrade !== null) return -1;
      if (bGrade !== null) return 1;
      return a.localeCompare(b, undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });
  }, [periodData, filters.testType]);

  // Test types derived from fetched period data filtered by selected level
  const availableTestTypes = useMemo(() => {
    if (!periodData?.classes || !Array.isArray(periodData.classes)) {
      const types = new Set();
      reportCardData.forEach((rc) => {
        (rc.examinations || []).forEach((ex) => {
          if (ex.testType) types.add(ex.testType);
        });
      });
      return Array.from(types).sort();
    }

    let candidateClasses = periodData.classes;

    if (filters.level && filters.level !== "all") {
      candidateClasses = candidateClasses.filter(
        (c) => c.level === filters.level,
      );
    }

    const testTypes = [
      ...new Set(
        candidateClasses
          .flatMap((c) => c.tests || [])
          .map((t) => t?.testType)
          .filter(Boolean),
      ),
    ].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }),
    );

    return testTypes;
  }, [periodData, filters.level, reportCardData]);

  const computedReportCardData = useMemo(() => {
    if (filters.testType.length === 0) return [];

    return reportCardData.map((rc) => {
      const examsToKeep = (rc.examinations || []).filter((ex) =>
        filters.testType.includes(ex.testType),
      );

      let cScore = 0;
      let cMax = 0;
      examsToKeep.forEach((exam) => {
        (exam.subjects || []).forEach((subj) => {
          // Always count maxScore (the exam had those marks available);
          // absent subjects contribute 0 to score.
          cMax += subj.maxScore || 0;
          if (!subj.absent) {
            cScore += subj.score || 0;
          }
        });
      });
      const cPct = cMax > 0 ? (cScore / cMax) * 100 : 0;

      return {
        ...rc,
        examinations: examsToKeep,
        cumulativeScore: cScore,
        cumulativeMaxScore: cMax,
        cumulativePct: cPct,
      };
    });
  }, [reportCardData, filters.testType]);

  const isReady = useMemo(() => {
    return filters.level !== "all" && filters.testType.length > 0;
  }, [filters.level, filters.testType]);

  // Helper to extract student reference ID without falling back to MongoDB _id
  const getStudentReferenceId = useCallback((student) => {
    if (!student) return "";
    const ref =
      student.referenceId != null ? String(student.referenceId).trim() : "";
    const sid = student._id != null ? String(student._id).trim() : "";
    if (
      !ref ||
      ref === "—" ||
      ref === "-" ||
      (sid && ref.toLowerCase() === sid.toLowerCase()) ||
      /^[a-f\d]{24}$/i.test(ref)
    ) {
      return "";
    }
    return ref;
  }, []);

  // Prepare table data from computedReportCardData
  const tableData = useMemo(() => {
    return computedReportCardData.map((rc, idx) => ({
      _id: rc.student?._id || `rc-${idx}`,
      sno: idx + 1,
      studentName:
        `${rc.student?.firstName || ""} ${rc.student?.lastName || ""}`.trim(),
      rollNo:
        rc.student?.rollNo != null && rc.student?.rollNo !== ""
          ? rc.student.rollNo
          : "—",
      referenceId: getStudentReferenceId(rc.student) || "—",
      cumulativePct:
        rc.cumulativePct != null ? Math.round(rc.cumulativePct * 100) / 100 : 0,
      _raw: rc, // keep computed data for PDF generation
    }));
  }, [computedReportCardData, getStudentReferenceId]);

  const handleGeneratePDFs = useCallback(
    async (
      studentsToProcess = computedReportCardData,
      targetId = "all",
      format = "single",
    ) => {
      if (!studentsToProcess.length) return;

      setGeneratingPdfTarget(targetId);
      setGeneratingPdfProgress({
        current: 0,
        total: studentsToProcess.length,
        format,
      });

      try {
        const [{ jsPDF }, { default: html2canvas }, { PDFDocument }, JSZipModule] =
          await Promise.all([
            import("jspdf"),
            import("html2canvas"),
            import("pdf-lib"),
            format === "zip" ? import("jszip") : Promise.resolve(null),
          ]);
        const JSZip = JSZipModule?.default || JSZipModule;

        const PAGE_W = 210; // width in mm (A4 width)
        const MARGIN = 5;
        const generatedDocs = [];

        for (let i = 0; i < studentsToProcess.length; i++) {
          setGeneratingPdfProgress({
            current: i + 1,
            total: studentsToProcess.length,
            format,
          });
          const rcData = studentsToProcess[i];

          const rcProps = {
            instituteInfo,
            studentName:
              `${rcData.student?.firstName || ""} ${rcData.student?.lastName || ""}`.trim(),
            rollNo: rcData.student?.rollNo || "",
            referenceId: getStudentReferenceId(rcData.student),
            level: filters.level,
            periodLabel: (() => {
              const p = periods.find((p) => p._id === filters.period);
              return p ? `${p.term} ${p.year}` : "";
            })(),
            examinations: rcData.examinations || [],
            cumulativeScore: rcData.cumulativeScore || 0,
            cumulativeMaxScore: rcData.cumulativeMaxScore || 0,
            cumulativePct: rcData.cumulativePct || 0,
            rank: null,
            totalStudents: null,
            gradeLetterMapping: instituteInfo?.gradeLetterMapping || [],
          };

          const container = document.createElement("div");
          container.style.position = "fixed";
          container.style.top = "0";
          container.style.left = "0";
          container.style.width = "900px";
          container.style.background = "#fff";
          container.style.zIndex = "-1";
          container.style.pointerEvents = "none";
          document.body.appendChild(container);

          const root = createRoot(container);
          root.render(
            <div
              id="rc-pdf-wrapper"
              style={{
                width: "794px",
                overflow: "hidden",
                background: "#fff",
                position: "relative",
              }}
            >
              <ReportCard {...rcProps} />
            </div>,
          );

          await new Promise((r) => setTimeout(r, 500));
          const imgs = Array.from(container.querySelectorAll("img"));
          await Promise.all(
            imgs.map(
              (img) =>
                new Promise((resolve) => {
                  if (img.complete) return resolve();
                  img.onload = resolve;
                  img.onerror = resolve;
                  setTimeout(resolve, 1500);
                }),
            ),
          );
          await new Promise((r) => setTimeout(r, 200));

          // Capture the wrapper div that controls the exact width
          const wrapperEl = container.querySelector("#rc-pdf-wrapper");

          const canvas = await html2canvas(wrapperEl, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            logging: false,
            windowWidth: 900,
            scrollY: -window.scrollY,
            scrollX: 0,
          });

          try {
            root.unmount();
          } catch {}
          container.remove();

          const imgData = canvas.toDataURL("image/jpeg", 0.9);
          const imgWidthMm = PAGE_W - MARGIN * 2;
          const imgHeightMm = (canvas.height / canvas.width) * imgWidthMm;
          const pageH = imgHeightMm + MARGIN * 2;

          // Use landscape when page is short to prevent jsPDF from
          // swapping width/height (portrait always makes shorter side = width)
          const isLandscape = pageH < PAGE_W;
          const singleDoc = new jsPDF({
            orientation: isLandscape ? "landscape" : "portrait",
            unit: "mm",
            format: isLandscape ? [pageH, PAGE_W] : [PAGE_W, pageH],
          });

          singleDoc.addImage(
            imgData,
            "JPEG",
            MARGIN,
            MARGIN,
            imgWidthMm,
            imgHeightMm,
          );

          generatedDocs.push({
            buffer: singleDoc.output("arraybuffer"),
            rcData,
          });
        }

        if (format === "zip") {
          const zip = new JSZip();
          const usedFileNames = new Set();

          for (let i = 0; i < generatedDocs.length; i++) {
            const { buffer, rcData } = generatedDocs[i];
            const student = rcData.student;
            const studentName =
              `${student?.firstName || ""} ${student?.lastName || ""}`.trim() ||
              "Student";

            const rawRoll =
              student?.rollNo != null ? String(student.rollNo).trim() : "";
            const rollNo =
              rawRoll !== "—" && rawRoll !== "-" ? rawRoll : "";

            const refId = getStudentReferenceId(student);

            const identifier = rollNo || refId;
            const baseName = identifier
              ? `${studentName} - ${identifier}`
              : studentName;
            const sanitizedBase = baseName
              .replace(/[/\\?%*:|"<>]/g, "_")
              .trim();

            let fileName = `${sanitizedBase}.pdf`;
            let counter = 1;
            while (usedFileNames.has(fileName.toLowerCase())) {
              fileName = `${sanitizedBase} (${counter}).pdf`;
              counter++;
            }
            usedFileNames.add(fileName.toLowerCase());

            zip.file(fileName, buffer);
          }

          const zipBlob = await zip.generateAsync({ type: "blob" });
          const url = URL.createObjectURL(zipBlob);
          const link = document.createElement("a");
          link.href = url;
          const zipBaseName = `Report Cards - ${filters.level || "Grade"}${
            filters.testType?.length ? ` - ${filters.testType.join(", ")}` : ""
          }`.replace(/[/\\?%*:|"<>]/g, "_");
          link.download = `${zipBaseName}.zip`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 5000);

          swalSuccess(
            studentsToProcess.length === 1
              ? "Report Card downloaded as ZIP!"
              : `${studentsToProcess.length} Report Cards downloaded as ZIP!`,
          );
        } else {
          const mergedPdf = await PDFDocument.create();
          for (const doc of generatedDocs) {
            const donorPdf = await PDFDocument.load(doc.buffer);
            const pages = await mergedPdf.copyPages(
              donorPdf,
              donorPdf.getPageIndices(),
            );
            pages.forEach((page) => mergedPdf.addPage(page));
          }

          const mergedBytes = await mergedPdf.save();
          const blob = new Blob([mergedBytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          const firstStudent = studentsToProcess[0]?.student;
          const rawFirstRoll =
            firstStudent?.rollNo != null
              ? String(firstStudent.rollNo).trim()
              : "";
          const firstRoll =
            rawFirstRoll !== "—" && rawFirstRoll !== "-" ? rawFirstRoll : "";
          const firstRef = getStudentReferenceId(firstStudent);
          const firstIdSuffix = firstRoll
            ? ` - Roll No ${firstRoll}`
            : firstRef
              ? ` - ID ${firstRef}`
              : "";

          const safeName =
            studentsToProcess.length === 1
              ? `Report Card - ${firstStudent?.firstName || ""} ${firstStudent?.lastName || ""}${firstIdSuffix}`.replace(
                  /[/\\?%*:|"<>]/g,
                  "_",
                )
              : `Report Cards - ${filters.level} - ${filters.testType.join(", ")}`.replace(
                  /[/\\?%*:|"<>]/g,
                  "_",
                );
          link.download = `${safeName}.pdf`;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 5000);

          swalSuccess(
            studentsToProcess.length === 1
              ? "Report Card downloaded!"
              : `${studentsToProcess.length} Report Cards downloaded!`,
          );
        }
      } catch (error) {
        console.error("PDF generation failed:", error);
        swalError("Failed to generate PDFs. Please try again.");
      } finally {
        setGeneratingPdfTarget(null);
        setGeneratingPdfProgress(null);
      }
    },
    [
      computedReportCardData,
      instituteInfo,
      filters,
      periods,
      getStudentReferenceId,
    ],
  );

  // ── Selection handlers ──
  const handleRowSelect = useCallback((e, id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((visibleIds, areAllSelected) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (areAllSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  // ── Generate single PDF blob (for sending) ──
  const generateSinglePdfBlob = useCallback(
    async (rcData) => {
      const [{ jsPDF }, { default: html2canvas }] = await Promise.all([
        import("jspdf"),
        import("html2canvas"),
      ]);

      const PAGE_W = 210;
      const MARGIN = 5;

      const rcProps = {
        instituteInfo,
        studentName:
          `${rcData.student?.firstName || ""} ${rcData.student?.lastName || ""}`.trim(),
        rollNo: rcData.student?.rollNo || "",
        referenceId: getStudentReferenceId(rcData.student),
        level: filters.level,
        periodLabel: (() => {
          const p = periods.find((p) => p._id === filters.period);
          return p ? `${p.term} ${p.year}` : "";
        })(),
        examinations: rcData.examinations || [],
        cumulativeScore: rcData.cumulativeScore || 0,
        cumulativeMaxScore: rcData.cumulativeMaxScore || 0,
        cumulativePct: rcData.cumulativePct || 0,
        rank: null,
        totalStudents: null,
        gradeLetterMapping: instituteInfo?.gradeLetterMapping || [],
      };

      const container = document.createElement("div");
      container.style.position = "fixed";
      container.style.top = "0";
      container.style.left = "0";
      container.style.width = "900px";
      container.style.background = "#fff";
      container.style.zIndex = "-1";
      container.style.pointerEvents = "none";
      document.body.appendChild(container);

      const root = createRoot(container);
      root.render(
        <div
          id="rc-pdf-wrapper"
          style={{
            width: "794px",
            overflow: "hidden",
            background: "#fff",
            position: "relative",
          }}
        >
          <ReportCard {...rcProps} />
        </div>,
      );

      await new Promise((r) => setTimeout(r, 500));
      const imgs = Array.from(container.querySelectorAll("img"));
      await Promise.all(
        imgs.map(
          (img) =>
            new Promise((resolve) => {
              if (img.complete) return resolve();
              img.onload = resolve;
              img.onerror = resolve;
              setTimeout(resolve, 1500);
            }),
        ),
      );
      await new Promise((r) => setTimeout(r, 200));

      const wrapperEl = container.querySelector("#rc-pdf-wrapper");
      const canvas = await html2canvas(wrapperEl, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        logging: false,
        windowWidth: 900,
        scrollY: -window.scrollY,
        scrollX: 0,
      });

      try {
        root.unmount();
      } catch {}
      container.remove();

      const imgData = canvas.toDataURL("image/jpeg", 0.9);
      const imgWidthMm = PAGE_W - MARGIN * 2;
      const imgHeightMm = (canvas.height / canvas.width) * imgWidthMm;
      const pageH = imgHeightMm + MARGIN * 2;

      const isLandscape = pageH < PAGE_W;
      const singleDoc = new jsPDF({
        orientation: isLandscape ? "landscape" : "portrait",
        unit: "mm",
        format: isLandscape ? [pageH, PAGE_W] : [PAGE_W, pageH],
      });

      singleDoc.addImage(
        imgData,
        "JPEG",
        MARGIN,
        MARGIN,
        imgWidthMm,
        imgHeightMm,
      );

      const arrayBuf = singleDoc.output("arraybuffer");

      // Pass through pdf-lib to normalize the PDF structure (prevents FormData/Multer corruption)
      const { PDFDocument } = await import("pdf-lib");
      const donorPdf = await PDFDocument.load(arrayBuf);
      const cleanPdf = await PDFDocument.create();
      const pages = await cleanPdf.copyPages(
        donorPdf,
        donorPdf.getPageIndices(),
      );
      pages.forEach((p) => cleanPdf.addPage(p));
      const cleanBytes = await cleanPdf.save();

      return new Blob([cleanBytes], { type: "application/pdf" });
    },
    [instituteInfo, filters, periods, getStudentReferenceId],
  );

  // ── Send report cards to students ──
  const handleSendReportCards = useCallback(
    async (studentsToSend, notificationType) => {
      if (!studentsToSend.length) return;

      const targetId =
        studentsToSend.length === 1 ? studentsToSend[0].student?._id : "all";
      setSendingTarget(targetId);
      setSendPhase("preparing");
      setSendProgress("Preparing to send report cards...");

      let successCount = 0;
      let failCount = 0;

      try {
        for (let i = 0; i < studentsToSend.length; i++) {
          const rcData = studentsToSend[i];
          const studentName =
            `${rcData.student?.firstName || ""} ${rcData.student?.lastName || ""}`.trim();
          const studentRoll = rcData.student?.rollNo;
          const displayStudent = studentRoll
            ? `${studentName} (Roll No: ${studentRoll})`
            : studentName;
          const studentId = rcData.student?._id;

          setCurrentSendStudentName(displayStudent);
          setSendPhase("generating");
          setSendProgress(
            `Generating PDF for ${displayStudent} (${i + 1}/${studentsToSend.length})...`,
          );

          try {
            const pdfBlob = await generateSinglePdfBlob(rcData);

            setSendPhase("sending");
            setSendProgress(
              `Sending to ${displayStudent} (${i + 1}/${studentsToSend.length})...`,
            );

            const formData = new FormData();
            const safeName = (studentName || studentId || "")
              .replace(/[/\\?%*:|"><]/g, "_")
              .trim();
            const rollSuffix = studentRoll ? `_${studentRoll}` : "";
            const filename = `${filters.testType} Report Card - ${safeName}${rollSuffix}.pdf`;
            formData.append("reportCard", pdfBlob, filename);
            formData.append("notificationType", notificationType);
            formData.append(
              "testType",
              filters.testType?.join(", ") || "Report Card",
            );

            const resp = await fetchWithAuth(
              `${serverUrl}/api/institute/send-report-card/${studentId}`,
              { method: "POST", body: formData },
            );

            if (resp && resp.ok) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (err) {
            console.error("Error sending report card for", studentId, err);
            failCount++;
          }
        }

        setSendPhase("completed");
        setSendProgress("All notifications sent!");
        setCurrentSendStudentName("");

        if (failCount > 0) {
          swalWarning(`Sent: ${successCount} successful, ${failCount} failed.`);
        } else {
          swalSuccess(
            `Report cards sent successfully to ${successCount} student${successCount !== 1 ? "s" : ""}!`,
          );
        }
      } catch (error) {
        console.error("handleSendReportCards error", error);
        swalError("Failed to send report card notifications!");
      } finally {
        setSendingTarget(null);
        setSendPhase("");
        setSendProgress("");
        setCurrentSendStudentName("");
      }
    },
    [generateSinglePdfBlob, filters.testType],
  );

  // ── Open download format modal ──
  const openDownloadModal = useCallback((studentsToDownload) => {
    setPendingDownloadStudents(studentsToDownload);
    setShowDownloadModal(true);
  }, []);

  const handleDownloadFormatSelect = useCallback(
    (format) => {
      setShowDownloadModal(false);
      handleGeneratePDFs(pendingDownloadStudents, "selected", format);
    },
    [pendingDownloadStudents, handleGeneratePDFs],
  );

  // ── Open notification type modal ──
  const openNotifyModal = useCallback((studentsToSend) => {
    setPendingSendStudents(studentsToSend);
    setShowNotifyModal(true);
  }, []);

  const handleNotifyTypeSelect = useCallback(
    (notificationType) => {
      setShowNotifyModal(false);
      handleSendReportCards(pendingSendStudents, notificationType);
    },
    [pendingSendStudents, handleSendReportCards],
  );

  const reportCardColumns = useMemo(
    () => [
      {
        key: "sno",
        label: "#",
        sortable: false,
      },
      {
        key: "studentName",
        label: "Student Name",
      },
      {
        key: "rollNo",
        label: "Roll No.",
      },
      {
        key: "referenceId",
        label: "Student ID",
      },
      {
        key: "cumulativePct",
        label: "Percentage",
        render: (item) => (
          <span style={{ fontWeight: 600 }}>
            {item.cumulativePct.toFixed(2)}%
          </span>
        ),
      },
      {
        key: "download",
        label: "Download",
        sortable: false,
        render: (item) => (
          <button
            className="btn-download"
            style={{
              padding: "0.4rem 0.8rem",
              fontSize: "0.85rem",
            }}
            onClick={() =>
              handleGeneratePDFs([item._raw], item._raw.student?._id)
            }
            disabled={generatingPdfTarget !== null}
          >
            {generatingPdfTarget === item._raw.student?._id ? (
              <Loader
                style={{
                  left: 0,
                  fontSize: "14px",
                }}
              />
            ) : (
              <i className="fa-regular fa-file-pdf" />
            )}
            &nbsp;PDF
          </button>
        ),
      },
      {
        key: "send",
        label: "Send",
        sortable: false,
        render: (item) => (
          <button
            className="btn-download"
            style={{
              padding: "0.4rem 0.8rem",
              fontSize: "0.85rem",
              background: "var(--color-1)",
            }}
            onClick={() => openNotifyModal([item._raw])}
            disabled={sendingTarget !== null || generatingPdfTarget !== null}
          >
            {sendingTarget === item._raw.student?._id ? (
              <Loader style={{ left: 0, fontSize: "14px" }} />
            ) : (
              <i className="fa-solid fa-paper-plane" />
            )}
            &nbsp;Send
          </button>
        ),
      },
    ],
    [generatingPdfTarget, handleGeneratePDFs, sendingTarget, openNotifyModal],
  );

  const handleExportExcel = async () => {
    if (!computedReportCardData.length) return;
    setExportingExcel(true);
    try {
      const ExcelJS = (await import("exceljs")).default;
      const wb = new ExcelJS.Workbook();
      wb.creator = "GradeMe AI";
      const ws = wb.addWorksheet("Report Card", {
        views: [{ state: "frozen", ySplit: 5 }],
      });

      // ── Gather all unique test types & subjects ──
      const testTypes = filters.testType;
      const subjectSet = new Set();
      computedReportCardData.forEach((rc) =>
        (rc.examinations || []).forEach((exam) =>
          (exam.subjects || []).forEach((s) => subjectSet.add(s.className)),
        ),
      );
      const subjects = [...subjectSet].sort((a, b) => a.localeCompare(b));

      // ── Color palette ──
      const brandGold = "FFFDE17";
      const brandOrange = "FFF09A21";
      const brandPink = "FFDA1C5C";
      const darkText = "FF23272F";
      const white = "FFFFFFFF";
      const lighterGold = "FFFFF8E1";
      const lightGray = "FFF8FAFC";
      const borderColor = "FFE2E8F0";
      const absentRed = "FFDC3545";

      const thinBorder = {
        top: { style: "thin", color: { argb: borderColor } },
        bottom: { style: "thin", color: { argb: borderColor } },
        left: { style: "thin", color: { argb: borderColor } },
        right: { style: "thin", color: { argb: borderColor } },
      };

      // ── Build column structure ──
      // Cols: S.No | Name | Roll No. | ID | [per testType: subjects(Score,%) + Total(Score, Max, %)] | Cumulative(Score, Max, %)
      const colsPerTestType = subjects.length * 2 + 3; // subj*(score+%) + total(score+max+%)
      const fixedCols = 4; // S.No, Name, Roll No., ID
      const cumulativeCols = 3; // Score, Max, %
      const totalCols =
        fixedCols + testTypes.length * colsPerTestType + cumulativeCols;

      // Set all columns with default width
      const columns = [];
      for (let i = 0; i < totalCols; i++) columns.push({ width: 12 });
      columns[0] = { width: 6 }; // S.No
      columns[1] = { width: 22 }; // Name
      columns[2] = { width: 14 }; // Roll No.
      columns[3] = { width: 14 }; // ID
      ws.columns = columns;

      // ── Row 1: Institute title ──
      const instituteName = instituteInfo?.name || "GradeMe AI";
      const periodLabel = periods.find((p) => p._id === filters.period);
      const periodStr = periodLabel
        ? `${periodLabel.term}-${periodLabel.year}`
        : "";

      ws.mergeCells(1, 1, 1, totalCols);
      const titleCell = ws.getCell(1, 1);
      titleCell.value = instituteName.toUpperCase();
      titleCell.font = { size: 16, bold: true, color: { argb: darkText } };
      titleCell.alignment = { horizontal: "center", vertical: "middle" };
      titleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: brandGold },
      };
      ws.getRow(1).height = 32;

      // ── Row 2: Subtitle ──
      ws.mergeCells(2, 1, 2, totalCols);
      const subtitleCell = ws.getCell(2, 1);
      subtitleCell.value = `Report Card  |  Level: ${filters.level}  |  Period: ${periodStr}  |  Generated: ${new Date().toLocaleDateString()}`;
      subtitleCell.font = { size: 10, bold: true, color: { argb: white } };
      subtitleCell.alignment = { horizontal: "center", vertical: "middle" };
      subtitleCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: brandOrange },
      };
      ws.getRow(2).height = 22;

      // ── Row 3: Empty spacer ──
      ws.getRow(3).height = 6;

      // ── Row 4: Group headers (Test Type bands + Cumulative) ──
      const row4 = ws.getRow(4);
      row4.height = 24;
      // Fixed cols header
      ws.mergeCells(4, 1, 5, 1); // S.No
      ws.mergeCells(4, 2, 5, 2); // Name
      ws.mergeCells(4, 3, 5, 3); // Roll No.
      ws.mergeCells(4, 4, 5, 4); // ID
      const fixedHeaders = ["#", "Student Name", "Roll No.", "Student ID"];
      for (let i = 0; i < fixedHeaders.length; i++) {
        const c = ws.getCell(4, i + 1);
        c.value = fixedHeaders[i];
        c.font = { size: 10, bold: true, color: { argb: darkText } };
        c.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: brandGold },
        };
        c.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
        c.border = thinBorder;
        // Also style merged row 5 cell
        const c5 = ws.getCell(5, i + 1);
        c5.font = c.font;
        c5.fill = c.fill;
        c5.alignment = c.alignment;
        c5.border = thinBorder;
      }

      // Test type group headers
      let colIdx = fixedCols + 1;
      const testTypeColors = [brandGold, lighterGold];
      testTypes.forEach((tt, ttIdx) => {
        const startCol = colIdx;
        const endCol = colIdx + colsPerTestType - 1;
        ws.mergeCells(4, startCol, 4, endCol);
        const ttCell = ws.getCell(4, startCol);
        ttCell.value = tt;
        ttCell.font = { size: 11, bold: true, color: { argb: darkText } };
        ttCell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: testTypeColors[ttIdx % 2] },
        };
        ttCell.alignment = { horizontal: "center", vertical: "middle" };
        ttCell.border = thinBorder;
        colIdx += colsPerTestType;
      });

      // Cumulative group header
      const cumStartCol = colIdx;
      ws.mergeCells(4, cumStartCol, 4, cumStartCol + cumulativeCols - 1);
      const cumHeaderCell = ws.getCell(4, cumStartCol);
      cumHeaderCell.value = "CUMULATIVE";
      cumHeaderCell.font = { size: 11, bold: true, color: { argb: white } };
      cumHeaderCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: brandPink },
      };
      cumHeaderCell.alignment = { horizontal: "center", vertical: "middle" };
      cumHeaderCell.border = thinBorder;

      // ── Row 5: Sub-headers (subject names + totals) ──
      const row5 = ws.getRow(5);
      row5.height = 22;
      colIdx = fixedCols + 1;
      testTypes.forEach((_, ttIdx) => {
        subjects.forEach((subj) => {
          const scoreCell = ws.getCell(5, colIdx);
          scoreCell.value = subj;
          scoreCell.font = { size: 8.5, bold: true, color: { argb: darkText } };
          scoreCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: testTypeColors[ttIdx % 2] },
          };
          scoreCell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
          scoreCell.border = thinBorder;
          colIdx++;

          const pctCell = ws.getCell(5, colIdx);
          pctCell.value = "%";
          pctCell.font = {
            size: 8.5,
            bold: true,
            color: { argb: brandOrange },
          };
          pctCell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: testTypeColors[ttIdx % 2] },
          };
          pctCell.alignment = { horizontal: "center", vertical: "middle" };
          pctCell.border = thinBorder;
          colIdx++;
        });
        // Total sub-headers
        ["Total", "Max", "%"].forEach((lbl) => {
          const c = ws.getCell(5, colIdx);
          c.value = lbl;
          c.font = { size: 9, bold: true, color: { argb: brandPink } };
          c.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: testTypeColors[ttIdx % 2] },
          };
          c.alignment = { horizontal: "center", vertical: "middle" };
          c.border = thinBorder;
          colIdx++;
        });
      });
      // Cumulative sub-headers
      ["Score", "Max", "%"].forEach((lbl) => {
        const c = ws.getCell(5, colIdx);
        c.value = lbl;
        c.font = { size: 9, bold: true, color: { argb: white } };
        c.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: brandPink },
        };
        c.alignment = { horizontal: "center", vertical: "middle" };
        c.border = thinBorder;
        colIdx++;
      });

      // ── Data rows ──
      const sortedStudents = [...computedReportCardData].sort((a, b) => {
        const rollA = a.student?.rollNo;
        const rollB = b.student?.rollNo;

        const hasA = rollA != null && String(rollA).trim() !== "";
        const hasB = rollB != null && String(rollB).trim() !== "";

        if (hasA && hasB) {
          const comp = String(rollA).trim().localeCompare(String(rollB).trim(), undefined, {
            numeric: true,
            sensitivity: "base",
          });
          if (comp !== 0) return comp;
        } else if (hasA) {
          return -1;
        } else if (hasB) {
          return 1;
        }

        const nameA = `${a.student?.firstName || ""} ${a.student?.lastName || ""}`.trim();
        const nameB = `${b.student?.firstName || ""} ${b.student?.lastName || ""}`.trim();
        return nameA.localeCompare(nameB);
      });

      sortedStudents.forEach((rc, sIdx) => {
        const rowNum = 6 + sIdx;
        const row = ws.getRow(rowNum);
        row.height = 20;
        const isEven = sIdx % 2 === 0;
        const rowFill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: isEven ? white : lightGray },
        };
        const baseFont = { size: 10, color: { argb: darkText } };

        // Fixed cols
        const snoCell = ws.getCell(rowNum, 1);
        snoCell.value = sIdx + 1;
        snoCell.font = {
          ...baseFont,
          bold: true,
          color: { argb: brandOrange },
        };
        snoCell.fill = rowFill;
        snoCell.alignment = { horizontal: "center", vertical: "middle" };
        snoCell.border = thinBorder;

        const nameCell = ws.getCell(rowNum, 2);
        nameCell.value =
          `${rc.student?.firstName || ""} ${rc.student?.lastName || ""}`.trim();
        nameCell.font = { ...baseFont, bold: true };
        nameCell.fill = rowFill;
        nameCell.alignment = { horizontal: "left", vertical: "middle" };
        nameCell.border = thinBorder;

        const rollNoCell = ws.getCell(rowNum, 3);
        rollNoCell.value = rc.student?.rollNo || "";
        rollNoCell.font = baseFont;
        rollNoCell.fill = rowFill;
        rollNoCell.alignment = { horizontal: "center", vertical: "middle" };
        rollNoCell.border = thinBorder;

        const idCell = ws.getCell(rowNum, 4);
        idCell.value = getStudentReferenceId(rc.student);
        idCell.font = baseFont;
        idCell.fill = rowFill;
        idCell.alignment = { horizontal: "center", vertical: "middle" };
        idCell.border = thinBorder;

        colIdx = fixedCols + 1;
        testTypes.forEach((tt) => {
          const exam = (rc.examinations || []).find((e) => e.testType === tt);
          subjects.forEach((subj) => {
            const subjData = exam?.subjects?.find((s) => s.className === subj);
            const scoreC = ws.getCell(rowNum, colIdx);
            const pctC = ws.getCell(rowNum, colIdx + 1);

            if (!subjData) {
              scoreC.value = "—";
              pctC.value = "—";
              scoreC.font = { ...baseFont, color: { argb: "FF999999" } };
              pctC.font = { ...baseFont, color: { argb: "FF999999" } };
            } else if (subjData.absent) {
              scoreC.value = "AB";
              pctC.value = "AB";
              scoreC.font = {
                ...baseFont,
                bold: true,
                color: { argb: absentRed },
              };
              pctC.font = {
                ...baseFont,
                bold: true,
                color: { argb: absentRed },
              };
            } else {
              scoreC.value = Math.round(subjData.score * 10) / 10;
              pctC.value = Math.round(subjData.percentage * 10) / 10;
              scoreC.font = baseFont;
              pctC.font = baseFont;
            }
            scoreC.fill = rowFill;
            pctC.fill = rowFill;
            scoreC.alignment = { horizontal: "center", vertical: "middle" };
            pctC.alignment = { horizontal: "center", vertical: "middle" };
            scoreC.border = thinBorder;
            pctC.border = thinBorder;
            colIdx += 2;
          });

          // Test total cols
          const totalScoreC = ws.getCell(rowNum, colIdx);
          const totalMaxC = ws.getCell(rowNum, colIdx + 1);
          const totalPctC = ws.getCell(rowNum, colIdx + 2);
          if (exam) {
            totalScoreC.value = Math.round((exam.totalScore || 0) * 10) / 10;
            totalMaxC.value = exam.totalMaxScore || 0;
            totalPctC.value = Math.round((exam.totalPct || 0) * 10) / 10;
          } else {
            totalScoreC.value = "—";
            totalMaxC.value = "—";
            totalPctC.value = "—";
          }
          [totalScoreC, totalMaxC, totalPctC].forEach((c) => {
            c.font = { ...baseFont, bold: true };
            c.fill = rowFill;
            c.alignment = { horizontal: "center", vertical: "middle" };
            c.border = thinBorder;
          });
          colIdx += 3;
        });

        // Cumulative cols
        const cumScoreC = ws.getCell(rowNum, colIdx);
        const cumMaxC = ws.getCell(rowNum, colIdx + 1);
        const cumPctC = ws.getCell(rowNum, colIdx + 2);
        cumScoreC.value = Math.round((rc.cumulativeScore || 0) * 10) / 10;
        cumMaxC.value = rc.cumulativeMaxScore || 0;
        cumPctC.value = Math.round((rc.cumulativePct || 0) * 10) / 10;
        [cumScoreC, cumMaxC, cumPctC].forEach((c) => {
          c.font = { size: 10, bold: true, color: { argb: brandPink } };
          c.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: isEven ? "FFFFF0F5" : "FFFFE4EC" },
          };
          c.alignment = { horizontal: "center", vertical: "middle" };
          c.border = thinBorder;
        });
      });

      // ── Download ──
      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        `Report Card - ${filters.level} - ${filters.testType.join(", ")}.xlsx`.replace(
          /[/\\?%*:|"|<>]/g,
          "_",
        );
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      swalSuccess("Excel report downloaded!");
    } catch (err) {
      console.error("Excel export failed:", err);
      swalError("Failed to export Excel. Please try again.");
    } finally {
      setExportingExcel(false);
    }
  };

  const selectedCount = selectedStudentIds.size;

  if (loadingInitial) {
    return (
      <div className="reportcard-loading">
        <Loader style={{ left: 0 }} />
      </div>
    );
  }

  return (
    <div className="dashboard">
      <motion.div
        className="filters-panel shadow-sm"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          position: "relative",
          zIndex: 100,
          marginBottom: "1.5rem",
        }}
      >
        <div className="filters-header">
          <h3>Generate Report Cards</h3>
          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
            {hasActiveFilters && (
              <button onClick={clearAllFilters} className="clear-filters-btn">
                Clear All
              </button>
            )}
          </div>
        </div>
        <div className="filters-grid">
          {!disableFilters.period && (
            <div className="filter-group">
              <label>📅 Period</label>
              <select
                value={filters.period}
                onChange={(e) => handleFilterChange("period", e.target.value)}
              >
                <option value="all">All Periods</option>
                {(periods || []).map((period) => (
                  <option key={period._id} value={period._id}>
                    {periodDisplayMap[period._id] || period._id}
                    {period.status === "Ongoing" && " (Ongoing)"}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="filter-group">
            <label>🎓 Level</label>
            <select
              value={filters.level}
              onChange={(e) => handleFilterChange("level", e.target.value)}
              disabled={fetchingPeriodData}
            >
              <option value="all">Select Level...</option>
              {levelOptions.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>📝 Test Type</label>
            <div className="tt-multiselect" ref={testTypeRef}>
              <button
                type="button"
                className="tt-multiselect-toggle"
                onClick={() => setIsTestTypeMenuOpen(!isTestTypeMenuOpen)}
                disabled={fetchingPeriodData || availableTestTypes.length === 0}
              >
                <div className="tt-multiselect-label">
                  {filters.testType.length === 0
                    ? "Select Test Type..."
                    : filters.testType.length === 1
                      ? filters.testType[0]
                      : `${filters.testType.length} Types Selected`}
                  <i
                    className={`fa-solid fa-chevron-${isTestTypeMenuOpen ? "up" : "down"}`}
                  />
                </div>
              </button>

              {isTestTypeMenuOpen && (
                <div className="tt-multiselect-menu">
                  {availableTestTypes.map((t) => (
                    <label key={t} className="tt-multiselect-item">
                      <input
                        type="checkbox"
                        checked={filters.testType.includes(t)}
                        onChange={() => toggleTestType(t)}
                      />
                      <span>{t}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {(fetchingData || fetchingPeriodData) && (
        <div className="reportcard-loading" style={{ padding: "3rem 0" }}>
          <Loader style={{ left: 0, top: 0 }} />
        </div>
      )}

      {isReady && !fetchingData && !fetchingPeriodData && (
        <AnimatePresence>
          {computedReportCardData.length > 0 ? (
            <>
              <div
                className="manage-container"
                style={{ position: "relative", zIndex: 1 }}
              >
                {/* ── Sending Progress Overlay ── */}
                {sendingTarget !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    style={{
                      background: "var(--color-1)",
                      borderRadius: "12px",
                      padding: "1rem 1.5rem",
                      marginBottom: "1rem",
                      color: "#fff",
                      display: "flex",
                      alignItems: "center",
                      gap: "1rem",
                      boxShadow: "0 4px 15px rgba(99, 102, 241, 0.3)",
                    }}
                  >
                    {sendPhase !== "completed" ? (
                      <Loader
                        style={{
                          left: 0,
                          top: 0,
                          fontSize: "1.2rem",
                          color: "#fff",
                        }}
                      />
                    ) : (
                      <i
                        className="fa-solid fa-circle-check"
                        style={{ fontSize: "1.4rem" }}
                      />
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: "0.95rem" }}>
                        {sendPhase === "completed"
                          ? "Completed!"
                          : "Sending Report Cards..."}
                      </div>
                      <div
                        style={{
                          fontSize: "0.85rem",
                          opacity: 0.9,
                          marginTop: "0.25rem",
                        }}
                      >
                        {sendProgress}
                      </div>
                      {currentSendStudentName && sendPhase !== "completed" && (
                        <div
                          style={{
                            fontSize: "0.8rem",
                            opacity: 0.75,
                            marginTop: "0.15rem",
                          }}
                        >
                          Current: {currentSendStudentName}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                <Table
                  data={tableData}
                  columns={reportCardColumns}
                  caption={`Preview Results (${computedReportCardData.length} students)`}
                  textAlign="center"
                  pagination={true}
                  isCheckBoxAvailable={true}
                  selectedRowIds={selectedStudentIds}
                  onRowSelect={handleRowSelect}
                  onSelectAll={handleSelectAll}
                  disableRowClickSelection={false}
                  searchActions={
                    <div
                      style={{
                        display: "flex",
                        gap: "0.5rem",
                        alignItems: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        className="publish-action-btn"
                        style={{
                          background:
                            "linear-gradient(135deg, #217346 0%, #2d8f5e 100%)",
                        }}
                        onClick={handleExportExcel}
                        disabled={
                          exportingExcel ||
                          generatingPdfTarget !== null ||
                          sendingTarget !== null
                        }
                      >
                        {exportingExcel ? (
                          <Loader
                            style={{
                              left: 0,
                              top: 0,
                              fontSize: "1rem",
                              color: "#fff",
                            }}
                          />
                        ) : (
                          <i className="fa-solid fa-file-excel" />
                        )}
                        &nbsp; Export Excel
                      </button>
                      <button
                        className="publish-action-btn"
                        style={{
                          cursor:
                            selectedCount === 0 ? "not-allowed" : "pointer",
                        }}
                        onClick={() => {
                          const selected = computedReportCardData.filter((rc) =>
                            selectedStudentIds.has(rc.student?._id),
                          );
                          if (selected.length === 0) {
                            swalWarning(
                              "Please select at least one student to generate PDFs.",
                            );
                            return;
                          }
                          openDownloadModal(selected);
                        }}
                        disabled={
                          selectedCount === 0 ||
                          generatingPdfTarget !== null ||
                          exportingExcel ||
                          sendingTarget !== null
                        }
                      >
                        {generatingPdfTarget === "selected" ? (
                          <>
                            <Loader
                              style={{
                                left: 0,
                                top: 0,
                                fontSize: "1rem",
                                color: "#fff",
                              }}
                            />
                            &nbsp; Generating
                            {generatingPdfProgress &&
                            generatingPdfProgress.total > 0
                              ? ` (${generatingPdfProgress.current}/${generatingPdfProgress.total})`
                              : "..."}
                          </>
                        ) : (
                          <>
                            <i className="fa-solid fa-download" />
                            &nbsp; Generate Selected PDFs
                            {selectedCount > 0 ? ` (${selectedCount})` : ""}
                          </>
                        )}
                      </button>
                      <button
                        className="publish-action-btn"
                        style={{
                          background: "var(--color-1)",
                          cursor:
                            selectedCount === 0 ? "not-allowed" : "pointer",
                        }}
                        onClick={() => {
                          const selected = computedReportCardData.filter((rc) =>
                            selectedStudentIds.has(rc.student?._id),
                          );
                          if (selected.length === 0) {
                            swalWarning(
                              "Please select at least one student to send report cards.",
                            );
                            return;
                          }
                          openNotifyModal(selected);
                        }}
                        disabled={
                          selectedCount === 0 ||
                          sendingTarget !== null ||
                          generatingPdfTarget !== null
                        }
                      >
                        {selectedCount === 0 ? (
                          <>
                            <i className="fa-solid  fa-paper-plane" />
                            &nbsp; Select Students First
                          </>
                        ) : (
                          <>
                            <i className="fa-solid  fa-paper-plane" />
                            &nbsp; Send to Selected
                            {selectedCount > 0 ? ` (${selectedCount})` : ""}
                          </>
                        )}
                      </button>
                    </div>
                  }
                />
              </div>
            </>
          ) : (
            <div className="empty-state">
              <i className="fa-solid fa-folder-open empty-icon" />
              <p>No report card data found for the selected filters.</p>
            </div>
          )}
        </AnimatePresence>
      )}

      {/* ── Download Format Modal ── */}
      {showDownloadModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowDownloadModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              background: "#fff",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "480px",
              width: "92%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            {/* Top Right Close Button */}
            <button
              type="button"
              onClick={() => setShowDownloadModal(false)}
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                border: "none",
                background: "#f1f5f9",
                color: "#64748b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                fontSize: "15px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#e2e8f0";
                e.currentTarget.style.color = "#0f172a";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#f1f5f9";
                e.currentTarget.style.color = "#64748b";
              }}
              aria-label="Close modal"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div style={{ marginBottom: "1.5rem" }}>
              <i
                className="fa-solid fa-file-arrow-down"
                style={{
                  fontSize: "2.5rem",
                  background: "var(--color-1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "0.75rem",
                  display: "block",
                }}
              />
              <h3
                style={{
                  margin: "0 0 0.5rem",
                  fontSize: "1.25rem",
                  color: "#23272F",
                  fontWeight: 700,
                }}
              >
                Download Report Cards
              </h3>
              <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>
                {pendingDownloadStudents.length === 1
                  ? "Download report card for 1 selected student"
                  : `Download report cards for ${pendingDownloadStudents.length} selected students`}
              </p>
              <p
                style={{
                  margin: "0.5rem 0 0",
                  fontSize: "0.85rem",
                  color: "#888",
                }}
              >
                Choose output format:
              </p>
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.85rem",
                textAlign: "left",
              }}
            >
              {/* Option 1: Single Merged PDF */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleDownloadFormatSelect("single")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "1.5px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-1)";
                  e.currentTarget.style.backgroundColor = "rgba(235, 94, 40, 0.04)";
                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(235, 94, 40, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "10px",
                    backgroundColor: "rgba(235, 94, 40, 0.1)",
                    color: "var(--color-1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  <i className="fa-regular fa-file-pdf" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.98rem",
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: "2px",
                    }}
                  >
                    Single PDF (Combined)
                  </div>
                  <div
                    style={{
                      fontSize: "0.82rem",
                      color: "#64748b",
                      lineHeight: 1.35,
                    }}
                  >
                    All selected report cards merged into a single PDF document (Current format).
                  </div>
                </div>
                <i
                  className="fa-solid fa-chevron-right"
                  style={{ color: "#94a3b8", fontSize: "13px" }}
                />
              </div>

              {/* Option 2: Separate PDFs (ZIP) */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleDownloadFormatSelect("zip")}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  border: "1.5px solid #e2e8f0",
                  backgroundColor: "#ffffff",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "var(--color-2, #da1c5c)";
                  e.currentTarget.style.backgroundColor = "rgba(218, 28, 92, 0.04)";
                  e.currentTarget.style.boxShadow = "0 4px 14px rgba(218, 28, 92, 0.12)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e2e8f0";
                  e.currentTarget.style.backgroundColor = "#ffffff";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    width: "44px",
                    height: "44px",
                    borderRadius: "10px",
                    backgroundColor: "rgba(218, 28, 92, 0.1)",
                    color: "var(--color-2, #da1c5c)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                    flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-file-zipper" />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: "0.98rem",
                      fontWeight: 700,
                      color: "#0f172a",
                      marginBottom: "2px",
                    }}
                  >
                    Separate PDFs (.ZIP)
                  </div>
                  <div
                    style={{
                      fontSize: "0.82rem",
                      color: "#64748b",
                      lineHeight: 1.35,
                    }}
                  >
                    Individual PDF files for each student packaged into a convenient ZIP archive.
                  </div>
                </div>
                <i
                  className="fa-solid fa-chevron-right"
                  style={{ color: "#94a3b8", fontSize: "13px" }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* ── Notification Type Modal ── */}
      {showNotifyModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => setShowNotifyModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: "16px",
              padding: "2rem",
              maxWidth: "420px",
              width: "90%",
              boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              textAlign: "center",
            }}
          >
            <div style={{ marginBottom: "1.5rem" }}>
              <i
                className="fa-solid fa-paper-plane"
                style={{
                  fontSize: "2.5rem",
                  background: "var(--color-1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  marginBottom: "0.75rem",
                  display: "block",
                }}
              />
              <h3
                style={{
                  margin: "0 0 0.5rem",
                  fontSize: "1.2rem",
                  color: "#23272F",
                }}
              >
                Send Report Card{pendingSendStudents.length !== 1 ? "s" : ""}
              </h3>
              <p style={{ margin: 0, fontSize: "0.9rem", color: "#666" }}>
                {pendingSendStudents.length === 1
                  ? `Send report card to ${pendingSendStudents[0]?.student?.firstName || ""} ${pendingSendStudents[0]?.student?.lastName || ""}${pendingSendStudents[0]?.student?.rollNo ? ` (Roll No: ${pendingSendStudents[0]?.student?.rollNo})` : ""}?`
                  : `Send report cards to ${pendingSendStudents.length} selected students?`}
              </p>
              <p
                style={{
                  margin: "0.5rem 0 0",
                  fontSize: "0.85rem",
                  color: "#999",
                }}
              >
                Choose how you'd like to send:
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",
              }}
            >
              <button
                className="publish-action-btn"
                style={{
                  width: "100%",
                  padding: "0.75rem 1.5rem",
                  fontSize: "0.95rem",
                  background: "var(--color-3)",
                  justifyContent: "center",
                }}
                onClick={() => handleNotifyTypeSelect("WhatsApp")}
              >
                <i
                  className="fa-brands fa-whatsapp"
                  style={{ fontSize: "1.2rem" }}
                />
                &nbsp; Send via WhatsApp
              </button>
              <button
                className="publish-action-btn"
                style={{
                  width: "100%",
                  padding: "0.75rem 1.5rem",
                  fontSize: "0.95rem",
                  background: "var(--color-2)",
                  justifyContent: "center",
                }}
                onClick={() => handleNotifyTypeSelect("email")}
              >
                <i
                  className="fa-solid fa-envelope"
                  style={{ fontSize: "1.1rem" }}
                />
                &nbsp; Send via Email
              </button>
              <button
                className="publish-action-btn"
                style={{
                  width: "100%",
                  padding: "0.75rem 1.5rem",
                  fontSize: "0.95rem",
                  background: "var(--color-4)",
                  justifyContent: "center",
                }}
                onClick={() => handleNotifyTypeSelect("push")}
              >
                <i
                  className="fa-solid fa-bell"
                  style={{ fontSize: "1.1rem" }}
                />
                &nbsp; Send via Push Notification
              </button>
              <button
                className="publish-action-btn"
                style={{
                  width: "100%",
                  padding: "0.75rem 1.5rem",
                  fontSize: "0.95rem",
                  background: "var(--color-1)",
                  justifyContent: "center",
                }}
                onClick={() => handleNotifyTypeSelect("all")}
              >
                <i
                  className="fa-solid fa-paper-plane"
                  style={{ fontSize: "1rem" }}
                />
                &nbsp; Send via All
              </button>
              <button
                onClick={() => setShowNotifyModal(false)}
                style={{
                  padding: "0.6rem 1.5rem",
                  fontSize: "0.9rem",
                  background: "transparent",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  color: "#666",
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default AdminReportCard;
